/**
 * GastoRepository - Repositorio unificado para Gastos
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Contiene toda la lógica de negocio de gastos, delegando las operaciones
 * de persistencia al IDatabaseAdapter específico de cada plataforma.
 */

import { v4 as uuidv4 } from 'uuid';
import { IGastoRepository } from '../interfaces/IGastoRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { Gasto, FiltroGastos } from '../interfaces/entities';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Gastos
 *
 * Este repositorio funciona tanto en web (con DexieAdapter) como en mobile (con SQLiteAdapter).
 */
export class GastoRepository extends BaseRepository<Gasto, string> implements IGastoRepository {
  private profileRepo: IProfileRepository | null = null;

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'gastos', 'gastos'); // tabla local, tabla Supabase

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    // Si se proporciona el profileRepository, inyectarlo
    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== CRUD Básico ====================

  async add(data: Omit<Gasto, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Gasto = {
      ...data,
      id,
      updated_at: now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      user_id: data.user_id || profile?.user_id || '',
      is_deleted: false,
      fecha_evento: data.fecha_evento || now
    };

    await this.db.insert('gastos', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<Gasto> & { id: string }): Promise<void> {
    // autoTimestamp de BaseRepository agrega updated_at automáticamente
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('gastos', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('gastos', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Soft Delete ====================
  // Estos métodos ahora vienen de BaseRepository (soporta soft delete habilitado)

  // ==================== Queries por Filtros ====================

  // ==================== Queries por Filtros ====================

  async getByFiltros(filtros: FiltroGastos): Promise<Gasto[]> {
    // Partimos de los registros de la empresa activa para no mezclar datos
    // de otras empresas.
    const base = (await this.getAllForActiveEmpresa()).filter(g => !g.is_deleted);

    const filtrados = base.filter(g => {
      if (filtros.fecha_inicio && g.fecha_evento < filtros.fecha_inicio) return false;
      if (filtros.fecha_fin && g.fecha_evento > filtros.fecha_fin) return false;
      if (filtros.concepto && !(g.concepto ?? '').toLowerCase().includes(filtros.concepto.toLowerCase())) return false;
      if (filtros.categoria && g.categoria !== filtros.categoria) return false;
      if (filtros.ubicacion_id && g.ubicacion_id !== filtros.ubicacion_id) return false;
      return true;
    });

    return filtrados.sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<Gasto[]> {
    // Filtramos por empresa activa primero para no mezclar datos de otras empresas.
    const deEmpresa = await this.getAllForActiveEmpresa();
    return deEmpresa
      .filter(g => !g.is_deleted && g.fecha_evento >= fechaInicio && g.fecha_evento <= fechaFin)
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getByCategoria(categoria: string): Promise<Gasto[]> {
    const all = await this.getAllForActiveEmpresa();
    return all
      .filter(g => !g.is_deleted && g.categoria === categoria)
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getByUbicacion(ubicacionId: string): Promise<Gasto[]> {
    const all = await this.getAllForActiveEmpresa();
    return all
      .filter(g => !g.is_deleted && g.ubicacion_id === ubicacionId)
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async searchByConcepto(concepto: string): Promise<Gasto[]> {
    const lower = concepto.toLowerCase();
    const all = await this.getAllForActiveEmpresa();
    return all
      .filter(g => !g.is_deleted && (g.concepto ?? '').toLowerCase().includes(lower))
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getTotalByPeriodo(fechaInicio: string, fechaFin: string): Promise<number> {
    const gastos = await this.getByPeriodo(fechaInicio, fechaFin);
    return gastos.reduce((total, g) => total + (g.monto || 0), 0);
  }

  async getGroupedByCategoria(fechaInicio?: string, fechaFin?: string): Promise<Map<string, number>> {
    const gastos = fechaInicio && fechaFin
      ? await this.getByPeriodo(fechaInicio, fechaFin)
      : await this.getAllForActiveEmpresa();

    const grouped = new Map<string, number>();
    gastos.forEach(g => {
      const cat = g.categoria || 'Sin categoría';
      grouped.set(cat, (grouped.get(cat) || 0) + (g.monto || 0));
    });
    return grouped;
  }

  async getByEmpresa(empresaId: string): Promise<Gasto[]> {
    return this.db.queryMultiple<Gasto>('gastos', [
      { field: 'is_deleted', operator: 'equals', value: false },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'fecha_evento', direction: 'desc' });
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================

  /**
   * Establece el repositorio de perfiles (inyección de dependencia)
   */
  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
